/**
 * Brain sobre o Supabase — trilhas B4 e B5, contra o backend das trilhas B1 e B6.
 *
 * Conversa, mensagem, projeto e preferência são lidos pelo PostgREST: a RLS
 * deixa cada pessoa ver só os próprios. Enviar, confirmar, cancelar, modelos,
 * texto, link e voz passam pelas funções `brain` e `brain-voz`, porque é lá que
 * a IA roda, que o teto do mês é conferido e que a ação executa — com a
 * permissão de quem clicou, nunca a da tela.
 */

import { supabase } from '@/supabase';
import { comClinicaAtiva } from '@/session/clinicaAtiva';
import { ErroDoBrain, motivoDaVozReserva, normalizarPreferencias, problemaDoAnexo } from './brain';
import type {
    AcaoDeTexto, AnexoDoBrain, ArquivoGeradoDoBrain, CampoDaProposta, ConversaDoBrain, DetalhesDaRecusa, EnvioAoBrain,
    FonteDaResposta, IdiomaDoBrain, LinkAprendido, MensagemDoBrain, ModelosDoBrain, PedidoDoBrain, PreferenciasDoBrain,
    FalaGerada, ProjetoDoBrain, PropostaDoBrain, ProvedorDeIa, ProvedorDoBrain, RespostaDoBrain, SituacaoDaProposta,
} from './brain';
import { clinicaAtiva } from './clinicaAtiva';
import { FluxoDePcm } from './fluxoDePcm';
import { nomeSeguroDoArquivo } from './midiaDaMensagem';
import type { PortaDeDados } from './porta';

type MetodosDoBrain = Pick<PortaDeDados, Extract<keyof PortaDeDados, `brain${string}`>>;

type Linha = Record<string, unknown>;

/** Endereço assinado de anexo e de arquivo gerado: curto, porque link encaminhado vaza. */
const VALIDADE_DO_ENDERECO_S = 300;

const PROVEDORES: ProvedorDeIa[] = ['anthropic', 'openai', 'gemini', 'grok', 'openrouter'];

function cliente() {
    if (!supabase) throw new Error('Servidor não configurado.');
    return supabase;
}

/** A clínica vai no corpo para a borda saber qual; quem autoriza é o token. */
function clinicaDoPedido(): string {
    const id = clinicaAtiva();
    if (!id) throw new Error('Sessão sem clínica: entre novamente.');
    return id;
}

async function usuarioDaSessao(): Promise<string> {
    const { data, error } = await cliente().auth.getUser();
    const uid = data.user?.id;
    if (error || !uid) throw new Error('Sessão expirada: entre novamente.');
    return uid;
}

function texto(valor: unknown): string {
    if (typeof valor === 'string') return valor;
    if (valor === null || valor === undefined) return '';
    return String(valor);
}

function opcional(valor: unknown): string | undefined {
    return typeof valor === 'string' && valor ? valor : undefined;
}

function linhas(valor: unknown): Linha[] {
    return Array.isArray(valor) ? valor.filter((v): v is Linha => typeof v === 'object' && v !== null) : [];
}

function objeto(valor: unknown): Linha {
    return typeof valor === 'object' && valor !== null && !Array.isArray(valor) ? valor as Linha : {};
}

/**
 * O corpo de erro da borda traz `erro` e, conforme o caso, `motivo` (texto do
 * anexo, ou código como `modelo_sem_chave`), `situacao` e `provedor`. O status e
 * esses detalhes decidem a frase da tela.
 */
async function lerRecusa(resposta: Response, falha: string): Promise<ErroDoBrain> {
    let mensagem = falha;
    const detalhes: DetalhesDaRecusa = {};
    try {
        const corpo = objeto(await resposta.clone().json());
        const motivo = opcional(corpo.motivo);
        const erro = opcional(corpo.erro);
        if (motivo) detalhes.motivo = motivo;
        if (opcional(corpo.situacao)) detalhes.situacao = texto(corpo.situacao);
        if (opcional(corpo.provedor)) detalhes.provedor = texto(corpo.provedor);
        if (opcional(corpo.anexo)) detalhes.anexo = texto(corpo.anexo);
        /* No 422 de anexo o `motivo` é a frase; nos códigos, `erro` é a frase. */
        mensagem = (detalhes.anexo && motivo) ? motivo : erro ?? (motivo && !/^[a-z_]+$/.test(motivo) ? motivo : falha);
    } catch {
        /* Corpo que não é JSON: fica a frase genérica, e o status continua valendo. */
    }
    return new ErroDoBrain(resposta.status, mensagem, detalhes);
}

interface OpcoesDaInvocacao {
    metodo?: 'GET' | 'POST';
    sinal?: AbortSignal;
}

async function invocar(nome: string, corpo: Linha | FormData | undefined, falha: string, opcoes: OpcoesDaInvocacao = {}): Promise<Linha> {
    const { data, error } = await cliente().functions.invoke(nome, {
        ...(corpo !== undefined ? { body: corpo } : {}),
        method: opcoes.metodo ?? 'POST',
        ...(opcoes.sinal ? { signal: opcoes.sinal } : {}),
    });
    if (opcoes.sinal?.aborted) throw new DOMException('Espera interrompida.', 'AbortError');
    if (error) {
        const contexto = (error as { context?: unknown }).context;
        if (contexto instanceof Response) throw await lerRecusa(contexto, falha);
        throw new ErroDoBrain(0, error instanceof Error ? error.message : falha);
    }
    if (typeof data !== 'object' || data === null) throw new ErroDoBrain(0, falha);
    return data as Linha;
}

function paraConversa(l: Linha): ConversaDoBrain {
    return {
        id: texto(l.id),
        titulo: texto(l.titulo) || 'Conversa sem título',
        criadoEm: texto(l.criado_em),
        atualizadoEm: texto(l.atualizado_em ?? l.criado_em),
        expiraEm: texto(l.expira_em),
        projetoId: opcional(l.projeto_id) ?? null,
    };
}

function paraAnexos(valor: unknown): AnexoDoBrain[] {
    return linhas(valor).map((a) => ({ caminho: texto(a.caminho), nome: texto(a.nome), tipo: texto(a.tipo) }));
}

function paraArquivo(l: Linha): ArquivoGeradoDoBrain {
    return { id: texto(l.id), nome: texto(l.nome), tipo: texto(l.tipo), url: texto(l.url ?? l.caminho) };
}

function paraCampos(valor: unknown): CampoDaProposta[] {
    return linhas(valor).map((c) => ({ rotulo: texto(c.rotulo), valor: texto(c.valor) }));
}

const SITUACOES: SituacaoDaProposta[] = ['pendente', 'confirmada', 'cancelada', 'falhou', 'expirada'];

/** O resultado pode vir como texto ou como objeto com `descricao`. */
function descricao(valor: unknown): string | undefined {
    if (typeof valor === 'string') return valor || undefined;
    if (typeof valor === 'object' && valor !== null && typeof (valor as Linha).descricao === 'string') {
        return (valor as Linha).descricao as string;
    }
    return undefined;
}

function paraProposta(l: Linha): PropostaDoBrain {
    const bruta = texto(l.situacao);
    /* Situação que a tela não conhece não pode oferecer "Confirmar": vira falha
       explicada, e não pendência. */
    const conhecida = (SITUACOES as string[]).includes(bruta);
    const resultado = descricao(l.resultado);
    const canalId = typeof l.resultado === 'object' && l.resultado !== null ? texto((l.resultado as Linha).canal_id) : '';
    const motivo = conhecida ? descricao(l.motivo) : `Situação desconhecida: ${bruta || 'vazia'}.`;
    return {
        id: texto(l.id),
        ferramenta: texto(l.ferramenta),
        resumo: texto(l.resumo),
        campos: paraCampos(l.campos),
        situacao: conhecida ? bruta as SituacaoDaProposta : 'falhou',
        ...(resultado ? { resultado } : {}),
        ...(motivo ? { motivo } : {}),
        ...(canalId ? { canalId } : {}),
    };
}

function paraFontes(valor: unknown): FonteDaResposta[] {
    return linhas(valor).map((f) => ({ titulo: texto(f.titulo) || texto(f.url), url: texto(f.url) })).filter((f) => /^https?:\/\//i.test(f.url));
}

function paraMensagem(l: Linha, arquivos: ArquivoGeradoDoBrain[], propostas: PropostaDoBrain[]): MensagemDoBrain {
    const provedor = opcional(l.provedor);
    const modelo = opcional(l.modelo);
    return {
        id: texto(l.id),
        papel: l.papel === 'usuario' ? 'usuario' : 'assistente',
        conteudo: texto(l.conteudo),
        criadoEm: texto(l.criado_em ?? l.criadoEm),
        anexos: paraAnexos(l.anexos),
        arquivos,
        propostas,
        ...(provedor ? { provedor } : {}),
        ...(modelo ? { modelo } : {}),
    };
}

/** Junta arquivos e propostas às mensagens pelo id da mensagem. */
function montarMensagens(mensagens: Linha[], arquivos: Linha[], propostas: Linha[], chave: 'mensagem_id' | 'mensagemId'): MensagemDoBrain[] {
    const arquivosPorMensagem = new Map<string, ArquivoGeradoDoBrain[]>();
    for (const l of arquivos) {
        const id = texto(l[chave]);
        arquivosPorMensagem.set(id, [...(arquivosPorMensagem.get(id) ?? []), paraArquivo(l)]);
    }
    const propostasPorMensagem = new Map<string, PropostaDoBrain[]>();
    for (const l of propostas) {
        const id = texto(l[chave]);
        propostasPorMensagem.set(id, [...(propostasPorMensagem.get(id) ?? []), paraProposta(l)]);
    }
    return mensagens.map((l) => paraMensagem(
        l, arquivosPorMensagem.get(texto(l.id)) ?? [], propostasPorMensagem.get(texto(l.id)) ?? [],
    ));
}

function extensaoDoAudio(tipo: string): string {
    if (tipo.includes('ogg')) return 'ogg';
    if (tipo.includes('mp4') || tipo.includes('aac')) return 'm4a';
    if (tipo.includes('wav')) return 'wav';
    if (tipo.includes('mpeg')) return 'mp3';
    return 'webm';
}

function paraProjeto(l: Linha): ProjetoDoBrain {
    return { id: texto(l.id), nome: texto(l.nome) || 'Projeto sem nome', criadoEm: texto(l.criado_em) };
}

/** Atualização que precisa ter alcançado uma linha: sem ela a RLS recusaria em silêncio. */
async function exigirLinha(consulta: PromiseLike<{ data: unknown; error: { message: string } | null }>, falha: string, nenhuma: string): Promise<void> {
    const { data, error } = await consulta;
    if (error) throw new Error(`${falha}: ${error.message}`);
    if (linhas(data).length === 0) throw new Error(nenhuma);
}

export const brainNoSupabase: MetodosDoBrain = {
    brainDemonstracao(): boolean {
        return false;
    },

    async brainConversas(filtro?: { projetoId: string | null }): Promise<ConversaDoBrain[]> {
        let consulta = cliente().from('brain_conversas')
            .select('id, titulo, criado_em, atualizado_em, expira_em, projeto_id')
            .order('atualizado_em', { ascending: false });
        if (filtro) consulta = filtro.projetoId === null ? consulta.is('projeto_id', null) : consulta.eq('projeto_id', filtro.projetoId);
        const { data, error } = await consulta;
        if (error) throw new Error(`Falha ao ler as conversas: ${error.message}`);
        return linhas(data).map(paraConversa);
    },

    async brainMensagens(conversaId: string): Promise<MensagemDoBrain[]> {
        const [m, a, p] = await Promise.all([
            cliente().from('brain_mensagens').select('id, conversa_id, papel, conteudo, anexos, provedor, modelo, criado_em')
                .eq('conversa_id', conversaId).order('criado_em'),
            cliente().from('brain_arquivos').select('*').eq('conversa_id', conversaId),
            cliente().from('brain_propostas').select('*').eq('conversa_id', conversaId),
        ]);
        const falha = m.error ?? a.error ?? p.error;
        if (falha) throw new Error(`Falha ao ler a conversa: ${falha.message}`);
        return montarMensagens(linhas(m.data), linhas(a.data), linhas(p.data), 'mensagem_id');
    },

    async brainEnviar(envio: EnvioAoBrain, sinal?: AbortSignal): Promise<RespostaDoBrain> {
        const r = await invocar('brain', {
            ...(envio.conversaId ? { conversaId: envio.conversaId } : {}),
            mensagem: envio.mensagem,
            anexos: envio.anexos ?? [],
            ...(envio.modo ? { modo: envio.modo } : {}),
            ...(envio.provedor ? { provedor: envio.provedor } : {}),
            ...(envio.modelo ? { modelo: envio.modelo } : {}),
            ...(envio.idioma ? { idioma: envio.idioma } : {}),
            ...(envio.buscarNaWeb ? { buscarNaWeb: true } : {}),
            ...(envio.buscarNosDocumentos ? { buscarNosDocumentos: true } : {}),
            ...(envio.projetoId && !envio.conversaId ? { projetoId: envio.projetoId } : {}),
            ...(envio.editarAPartirDe && envio.conversaId ? { editarAPartirDe: envio.editarAPartirDe } : {}),
            ...(envio.pedidoId ? { pedidoId: envio.pedidoId } : {}),
            ...(envio.modoVoz ? { modoVoz: true } : {}),
            clinicaId: clinicaDoPedido(),
        }, 'A VecchIA não respondeu.', { sinal });
        const bruta = objeto(r.mensagem);
        const consumo = objeto(r.consumo);
        const daPessoa = objeto(r.mensagemDaPessoa);
        const modelo = objeto(r.modelo);
        const fontes = paraFontes(r.fontes);
        const documentos = linhas(r.documentos).map((d) => ({ titulo: texto(d.titulo) })).filter((d) => d.titulo);
        const avisos = Array.isArray(r.avisos) ? r.avisos.map(texto).filter(Boolean) : [];
        const mensagem = paraMensagem(
            { ...bruta, papel: 'assistente', provedor: bruta.provedor ?? modelo.provedor, modelo: bruta.modelo ?? modelo.modelo },
            linhas(bruta.arquivos).map(paraArquivo),
            linhas(bruta.propostas).map(paraProposta),
        );
        return {
            conversaId: texto(r.conversaId),
            mensagem: { ...mensagem, fontes, documentos, avisos },
            consumo: { tokens: Number(consumo.tokens ?? 0) || 0 },
            pedidoId: opcional(r.pedidoId) ?? null,
            repetido: r.repetido === true,
            mensagemDaPessoa: opcional(daPessoa.id) ? { id: texto(daPessoa.id), criadoEm: texto(daPessoa.criadoEm) } : null,
            modelo: opcional(modelo.provedor) ? { provedor: texto(modelo.provedor), modelo: texto(modelo.modelo) } : null,
            fontes,
            documentos,
            avisos,
            removidas: Number(r.removidas ?? 0) || 0,
            fala: opcional(r.fala) ?? null,
        };
    },

    async brainPedido(pedidoId: string): Promise<PedidoDoBrain> {
        const r = await invocar(`brain/pedidos/${encodeURIComponent(pedidoId)}`, undefined, 'Não foi possível consultar o pedido.', { metodo: 'GET' });
        switch (r.situacao) {
            case 'processando':
                return { situacao: 'processando' };
            case 'falhou':
                return { situacao: 'falhou', erro: texto(r.erro) || 'O servidor não conseguiu responder.' };
            case 'pronta':
                return {
                    situacao: 'pronta',
                    conversaId: texto(r.conversaId),
                    mensagens: montarMensagens(linhas(r.mensagens), linhas(r.arquivos), linhas(r.propostas), 'mensagemId'),
                    fala: opcional(r.fala) ?? null,
                };
            default:
                return { situacao: 'desconhecido' };
        }
    },

    async brainEnviarAnexo(arquivo: File, conversaId?: string): Promise<AnexoDoBrain> {
        const problema = problemaDoAnexo(arquivo);
        if (problema) throw new Error(problema);
        const uid = await usuarioDaSessao();

        /* O caminho é o do contrato do balde: clínica, pessoa e conversa. A
           política do storage confere os dois primeiros contra o token. */
        const caminho = `${clinicaDoPedido()}/${uid}/${conversaId ?? 'rascunho'}/${crypto.randomUUID()}-${nomeSeguroDoArquivo(arquivo.name)}`;
        /* Os bytes, e não o `File`: ver a nota em `enviarAnexoDaFicha`. */
        const bytes = await arquivo.arrayBuffer();
        const { error } = await cliente().storage.from('brain').upload(caminho, bytes, {
            contentType: arquivo.type || 'application/octet-stream', upsert: false,
        });
        if (error) throw new Error(`Não foi possível enviar ${arquivo.name}: ${error.message}`);
        return { caminho, nome: arquivo.name, tipo: arquivo.type };
    },

    async brainConfirmarProposta(id: string): Promise<PropostaDoBrain> {
        const r = await invocar(`brain/propostas/${encodeURIComponent(id)}/confirmar`, { clinicaId: clinicaDoPedido() }, 'Não foi possível confirmar a ação.');
        return paraProposta((typeof r.proposta === 'object' && r.proposta !== null ? r.proposta : r) as Linha);
    },

    async brainCancelarProposta(id: string): Promise<PropostaDoBrain> {
        const r = await invocar(`brain/propostas/${encodeURIComponent(id)}/cancelar`, { clinicaId: clinicaDoPedido() }, 'Não foi possível cancelar a ação.');
        return paraProposta((typeof r.proposta === 'object' && r.proposta !== null ? r.proposta : r) as Linha);
    },

    async brainApagarConversa(id: string): Promise<void> {
        /* Com o `select`, apagar zero linhas aparece: sem ele a RLS recusaria em
           silêncio e a tela diria que apagou. */
        await exigirLinha(
            cliente().from('brain_conversas').delete().eq('id', id).select('id'),
            'Falha ao apagar a conversa', 'A conversa não foi apagada: ela não existe mais ou não é sua.',
        );
    },

    async brainRenomearConversa(id: string, titulo: string): Promise<void> {
        const limpo = titulo.trim().slice(0, 120);
        if (!limpo) throw new Error('Dê um nome à conversa.');
        await exigirLinha(
            cliente().from('brain_conversas').update({ titulo: limpo }).eq('id', id).select('id'),
            'Falha ao renomear a conversa', 'A conversa não foi renomeada: ela não existe mais ou não é sua.',
        );
    },

    async brainMoverConversa(id: string, projetoId: string | null): Promise<void> {
        await exigirLinha(
            cliente().from('brain_conversas').update({ projeto_id: projetoId }).eq('id', id).select('id'),
            'Falha ao mudar a conversa de projeto', 'A conversa não mudou de projeto: ela não existe mais ou não é sua.',
        );
    },

    async brainProjetos(): Promise<ProjetoDoBrain[]> {
        const { data, error } = await cliente().from('brain_projetos')
            .select('id, nome, criado_em')
            .eq('clinica_id', clinicaDoPedido())
            .is('arquivado_em', null)
            .order('criado_em', { ascending: false });
        if (error) throw new Error(`Falha ao ler os projetos: ${error.message}`);
        return linhas(data).map(paraProjeto);
    },

    async brainCriarProjeto(nome: string): Promise<ProjetoDoBrain> {
        const limpo = nome.trim().slice(0, 80);
        if (!limpo) throw new Error('Informe o nome do projeto.');
        const { data, error } = await cliente().from('brain_projetos')
            .insert({ clinica_id: clinicaDoPedido(), usuario_id: await usuarioDaSessao(), nome: limpo })
            .select('id, nome, criado_em')
            .single();
        if (error) throw new Error(`Não foi possível criar o projeto: ${error.message}`);
        return paraProjeto(objeto(data));
    },

    async brainRenomearProjeto(id: string, nome: string): Promise<void> {
        const limpo = nome.trim().slice(0, 80);
        if (!limpo) throw new Error('Informe o nome do projeto.');
        await exigirLinha(
            cliente().from('brain_projetos').update({ nome: limpo }).eq('id', id).select('id'),
            'Falha ao renomear o projeto', 'O projeto não foi renomeado: ele não existe mais ou não é seu.',
        );
    },

    async brainArquivarProjeto(id: string): Promise<void> {
        await exigirLinha(
            cliente().from('brain_projetos').update({ arquivado_em: new Date().toISOString() }).eq('id', id).select('id'),
            'Falha ao arquivar o projeto', 'O projeto não foi arquivado: ele não existe mais ou não é seu.',
        );
    },

    async brainPreferencias(): Promise<PreferenciasDoBrain> {
        const { data, error } = await cliente().from('brain_preferencias')
            .select('*')
            .eq('clinica_id', clinicaDoPedido())
            .maybeSingle();
        if (error) throw new Error(`Falha ao ler as preferências: ${error.message}`);
        return normalizarPreferencias(data ?? {});
    },

    async brainSalvarPreferencias(preferencias: PreferenciasDoBrain): Promise<PreferenciasDoBrain> {
        const p = normalizarPreferencias(preferencias);
        /* O objeto inteiro, com `on_conflict`: é o que o contrato pede, e o upsert
           do cliente manda `resolution=merge-duplicates`. */
        const { data, error } = await cliente().from('brain_preferencias')
            .upsert({
                usuario_id: await usuarioDaSessao(),
                clinica_id: clinicaDoPedido(),
                comportamento: p.comportamento,
                instrucoes: p.instrucoes,
                habilidades: p.habilidades,
                voz: p.voz,
                idioma: p.idioma,
            }, { onConflict: 'usuario_id,clinica_id' })
            .select('*')
            .single();
        if (error) throw new Error(`Não foi possível salvar as preferências: ${error.message}`);
        return normalizarPreferencias(data);
    },

    async brainModelos(): Promise<ModelosDoBrain> {
        const r = await invocar('brain/modelos', undefined, 'Não foi possível ler os modelos.', { metodo: 'GET' });
        const provedores: ProvedorDoBrain[] = linhas(r.provedores)
            .filter((p) => (PROVEDORES as string[]).includes(texto(p.provedor)))
            .map((p) => ({
                provedor: texto(p.provedor) as ProvedorDeIa,
                nome: texto(p.nome) || texto(p.provedor),
                buscaNaWeb: p.buscaNaWeb === true,
                modelos: linhas(p.modelos).map((m) => ({ id: texto(m.id), nome: texto(m.nome) || texto(m.id), descricao: texto(m.descricao) })).filter((m) => m.id),
            }));
        const padrao = objeto(r.padrao);
        const voz = objeto(r.voz);
        return {
            provedores,
            padrao: (PROVEDORES as string[]).includes(texto(padrao.provedor))
                ? { provedor: texto(padrao.provedor) as ProvedorDeIa, modelo: texto(padrao.modelo) || 'auto' }
                : null,
            documentos: r.documentos === true,
            voz: { transcrever: voz.transcrever === true, falar: voz.falar === true },
        };
    },

    async brainTexto(acao: AcaoDeTexto, textoOriginal: string, idioma?: IdiomaDoBrain): Promise<string> {
        const r = await invocar('brain/texto', {
            acao, texto: textoOriginal.slice(0, 4000), ...(idioma ? { idioma } : {}), clinicaId: clinicaDoPedido(),
        }, 'A IA não conseguiu trabalhar o texto.');
        const saida = texto(r.texto).trim();
        if (!saida) throw new ErroDoBrain(0, 'A IA não devolveu texto.');
        return saida;
    },

    async brainAprenderLink(url: string): Promise<LinkAprendido> {
        const r = await invocar('brain/link', { url, clinicaId: clinicaDoPedido() }, 'Não foi possível ler o endereço.');
        return {
            url: texto(r.url) || url,
            titulo: texto(r.titulo) || url,
            tipo: texto(r.tipo),
            texto: texto(r.texto),
            truncado: r.truncado === true,
        };
    },

    async brainTranscrever(audio: Blob, idioma?: IdiomaDoBrain): Promise<string> {
        if (audio.size === 0) throw new Error('A gravação ficou vazia: tente de novo, falando perto do microfone.');
        const formulario = new FormData();
        formulario.append('audio', audio, `gravacao.${extensaoDoAudio(audio.type)}`);
        formulario.append('clinicaId', clinicaDoPedido());
        if (idioma) formulario.append('idioma', idioma);
        const r = await invocar('brain-voz/transcrever', formulario, 'Não foi possível transcrever o áudio.');
        return texto(r.texto).trim();
    },

    async brainFalar(fala: string, opcoes: { idioma?: IdiomaDoBrain; fluxo?: boolean } = {}): Promise<FalaGerada> {
        /* Direto pelo `fetch`: o cliente das funções lê como texto a resposta
           `audio/*`, e texto corrompe o binário. */
        const base = import.meta.env.VITE_SUPABASE_URL;
        const chave = import.meta.env.VITE_SUPABASE_ANON_KEY;
        if (!base || !chave) throw new Error('Servidor não configurado.');
        const { data } = await cliente().auth.getSession();
        const token = data.session?.access_token;
        if (!token) throw new Error('Sessão expirada: entre novamente.');

        const resposta = await fetch(`${base}/functions/v1/brain-voz/falar`, {
            method: 'POST',
            headers: comClinicaAtiva({ apikey: chave, Authorization: `Bearer ${token}`, 'Content-Type': 'application/json' }),
            body: JSON.stringify({
                texto: fala,

                ...(opcoes.idioma ? { idioma: opcoes.idioma } : {}),
                ...(opcoes.fluxo ? { fluxo: true } : {}),
                clinicaId: clinicaDoPedido(),
            }),
        });
        if (!resposta.ok) throw await lerRecusa(resposta, 'Não foi possível gerar o áudio.');
        /* Quem falou: a tela avisa, discretamente, quando não foi a Gacrux. O CORS da borda
           expõe os cabeçalhos. */
        const provedor = resposta.headers.get('X-Brain-Provedor');
        const voz = resposta.headers.get('X-Brain-Voz');
        /* O motivo separa "espere um minuto" de "acabou por hoje" — sem ele o aviso da tela
           dizia só que a Gacrux não respondeu, e isso parece defeito. */
        const reserva = motivoDaVozReserva(resposta.headers.get('X-Brain-Reserva'));
        const reservaAte = resposta.headers.get('X-Brain-Reserva-Ate');
        /* Em fluxo, o PCM cru começa a tocar antes de a borda terminar de gerar. Mesmo pedindo
           fluxo, a fala do cache e a reserva da OpenAI chegam inteiras — por isso o tipo decide. */
        if ((resposta.headers.get('Content-Type') ?? '').startsWith('audio/pcm') && resposta.body) {
            const taxa = Number(resposta.headers.get('X-Brain-Taxa')) || 24000;
            return { audio: new Blob([], { type: 'audio/wav' }), fluxo: FluxoDePcm.lendo(resposta.body, taxa), provedor, voz, reserva, reservaAte };
        }
        return { audio: await resposta.blob(), provedor, voz, reserva, reservaAte };
    },

    async brainAbrirArquivo(caminho: string): Promise<string> {
        /* Endereço já assinado pela borda passa como veio; caminho no balde
           ganha assinatura agora, no clique. */
        if (/^https:\/\//i.test(caminho)) return caminho;
        const { data, error } = await cliente().storage.from('brain').createSignedUrl(caminho, VALIDADE_DO_ENDERECO_S);
        if (error || !data?.signedUrl) throw new Error(`Não foi possível abrir o arquivo: ${error?.message ?? 'sem endereço'}`);
        return data.signedUrl;
    },
};
